/**
 * waap-aiwaf-custom-rules-tab.tsx — AI-WAF Custom Rules tab (B4 batch).
 *
 * Account-level user-defined WAF detection rules (custom_strategies).
 * Service-scoped endpoints. List + create / edit / delete.
 * All mutations via ActAsMutationConfirm.
 *
 * Endpoints:
 *   GET    /services/:serviceId/waap/custom-rules
 *   POST   /services/:serviceId/waap/custom-rules
 *   PUT    /services/:serviceId/waap/custom-rules/:ruleId
 *   DELETE /services/:serviceId/waap/custom-rules/:ruleId
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import {
  Card, CardHeader, CardTitle, CardBody,
  Button, IconButton, Input, Textarea, Badge, Skeleton,
} from '@vnetwork/ui/primitives';
import { EmptyState } from '@vnetwork/ui/data';
import { Field } from '@vnetwork/ui/forms';
import { TabError } from '@/pages/customers/detail/tab-states';
import { ActAsMutationConfirm } from '@/pages/operations/detail/cdn/act-as-mutation-confirm';
import { actAsClient } from '@/api/act-as-client';
import { actAsWAAPKeys } from '@/api/act-as-waap';
import {
  useActAsWAAPCustomRules,
  type CustomRule,
} from '@/api/act-as-waap-rules';

/* ── Helpers ─────────────────────────────────────────────────────────────── */

function parseIdList(text: string): number[] {
  return text
    .split(/[\s,]+/)
    .map((s) => parseInt(s.trim(), 10))
    .filter((n) => !isNaN(n));
}

const EMPTY_RULE: CustomRule = {
  name: '',
  description: '',
  sites: [],
  config: {},
};

interface Pending {
  kind: 'create' | 'update' | 'delete';
  rule: CustomRule;
}

/* ── Root component ──────────────────────────────────────────────────────── */

interface WaapAiwafCustomRulesTabProps {
  serviceId: string;
}

/**
 * AI-WAF Custom Rules tab — list + CRUD for service-scoped custom WAF rules.
 * Config is a free-form JSON object (vendor detection shape).
 */
export function WaapAiwafCustomRulesTab({ serviceId }: WaapAiwafCustomRulesTabProps) {
  const { t } = useTranslation();
  const rulesQ = useActAsWAAPCustomRules(serviceId);

  const [formOpen, setFormOpen]     = useState(false);
  const [editing, setEditing]       = useState<CustomRule | null>(null);
  const [draft, setDraft]           = useState<CustomRule>(EMPTY_RULE);
  const [sitesDraft, setSitesDraft] = useState('');
  const [configDraft, setConfigDraft] = useState('{}');
  const [pending, setPending]       = useState<Pending | null>(null);

  function openNew() {
    setEditing(null);
    setDraft({ ...EMPTY_RULE });
    setSitesDraft('');
    setConfigDraft('{}');
    setFormOpen(true);
  }

  function openEdit(rule: CustomRule) {
    setEditing(rule);
    setDraft({ ...rule });
    setSitesDraft((rule.sites ?? []).join(', '));
    setConfigDraft(JSON.stringify(rule.config ?? {}, null, 2));
    setFormOpen(true);
  }

  function closeForm() {
    setFormOpen(false);
    setEditing(null);
  }

  function parseConfig(): Record<string, unknown> {
    try { return JSON.parse(configDraft) as Record<string, unknown>; }
    catch { return {}; }
  }

  function handleSubmit() {
    const payload: CustomRule = {
      ...draft,
      sites: parseIdList(sitesDraft),
      config: parseConfig(),
    };
    setPending({ kind: editing ? 'update' : 'create', rule: payload });
  }

  function buildMutationFn(p: Pending) {
    const base = `/services/${serviceId}/waap/custom-rules`;
    if (p.kind === 'create') return () => actAsClient.post(base, p.rule);
    if (p.kind === 'update') return () => actAsClient.put(`${base}/${p.rule.id}`, p.rule);
    return () => actAsClient.delete(`${base}/${p.rule.id}`);
  }

  function patch(key: keyof CustomRule, value: unknown) {
    setDraft((prev) => ({ ...prev, [key]: value }));
  }

  const isValid = draft.name.trim() !== '';

  if (rulesQ.isLoading) return <Skeleton style={{ width: '100%', height: 120 }} />;
  if (rulesQ.isError) {
    return (
      <TabError
        title={t('waap_domain.custom_rules_load_error', 'Failed to load custom rules')}
        onRetry={() => rulesQ.refetch()}
      />
    );
  }

  const rules = rulesQ.data ?? [];

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ margin: 0, fontSize: 'var(--vn-font-size-sm)', fontWeight: 600 }}>
          {t('waap_domain.custom_rules_title', 'Custom WAF Rules')}
          <Badge style={{ marginLeft: 'var(--vn-space-2)' }}>{rules.length}</Badge>
        </h3>
        <Button size="sm" variant="secondary" onClick={openNew}>
          <Plus size={13} />
          {t('waap_domain.custom_rules_add', 'Add rule')}
        </Button>
      </div>

      {rules.length === 0 && !formOpen ? (
        <EmptyState
          title={t('waap_domain.custom_rules_empty', 'No custom rules')}
          description={t('waap_domain.custom_rules_empty_desc', 'Create custom WAF detection rules to extend built-in protection.')}
        />
      ) : (
        <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
          {rules.map((rule) => (
            <Card key={rule.id ?? rule.name}>
              <CardBody style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-3)', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 'var(--vn-font-size-sm)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {rule.name}
                  </div>
                  {rule.description && (
                    <div style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)', marginTop: 2 }}>
                      {rule.description}
                    </div>
                  )}
                  {(rule.sites?.length ?? 0) > 0 && (
                    <div style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>
                      {t('waap_domain.custom_rules_sites', 'Sites: {{ids}}', { ids: rule.sites!.join(', ') })}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 'var(--vn-space-1)' }}>
                  <IconButton size="sm" variant="ghost" aria-label={t('common.edit', 'Edit')} onClick={() => openEdit(rule)}>
                    <Pencil size={13} />
                  </IconButton>
                  <IconButton
                    size="sm"
                    variant="ghost"
                    aria-label={t('common.delete', 'Delete')}
                    onClick={() => setPending({ kind: 'delete', rule })}
                  >
                    <Trash2 size={13} />
                  </IconButton>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}

      {formOpen && (
        <Card>
          <CardHeader>
            <CardTitle style={{ fontSize: 'var(--vn-font-size-sm)' }}>
              {editing
                ? t('waap_domain.custom_rules_edit', 'Edit custom rule')
                : t('waap_domain.custom_rules_new', 'New custom rule')}
            </CardTitle>
          </CardHeader>
          <CardBody style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 'var(--vn-space-3)' }}>
              <Field label={t('waap_domain.custom_rules_field_name', 'Name')}>
                {(p) => (
                  <Input {...p} value={draft.name} onChange={(e) => patch('name', e.target.value)} placeholder="Rule name" />
                )}
              </Field>
              <Field label={t('waap_domain.custom_rules_field_desc', 'Description')}>
                {(p) => (
                  <Input {...p} value={draft.description ?? ''} onChange={(e) => patch('description', e.target.value)} placeholder="Optional" />
                )}
              </Field>
              <Field label={t('waap_domain.custom_rules_field_sites', 'Site IDs (comma-sep)')}>
                {(p) => (
                  <Input {...p} value={sitesDraft} onChange={(e) => setSitesDraft(e.target.value)} placeholder="1, 2, 3" />
                )}
              </Field>
            </div>
            <Field
              label={t('waap_domain.custom_rules_field_config', 'Config (JSON)')}
              description={t('waap_domain.custom_rules_field_config_desc', 'Detection rule conditions as a vendor JSON object. See Conversant WAAP custom rule docs.')}
            >
              {(p) => (
                <Textarea
                  {...p}
                  value={configDraft}
                  onChange={(e) => setConfigDraft(e.target.value)}
                  rows={6}
                  style={{ fontFamily: 'monospace', fontSize: 'var(--vn-font-size-xs)' }}
                />
              )}
            </Field>
            <div style={{ display: 'flex', gap: 'var(--vn-space-2)', justifyContent: 'flex-end' }}>
              <Button size="sm" variant="secondary" onClick={closeForm}>
                {t('common.cancel', 'Cancel')}
              </Button>
              <Button size="sm" variant="primary" disabled={!isValid} onClick={handleSubmit}>
                {editing ? t('common.save', 'Save') : t('common.create', 'Create')}
              </Button>
            </div>
          </CardBody>
        </Card>
      )}

      {pending && (
        <ActAsMutationConfirm
          open
          onOpenChange={(open) => { if (!open) setPending(null); }}
          title={
            pending.kind === 'delete'
              ? t('waap_domain.custom_rules_delete_title', 'Delete custom rule')
              : pending.kind === 'create'
              ? t('waap_domain.custom_rules_create_title', 'Create custom rule')
              : t('waap_domain.custom_rules_update_title', 'Update custom rule')
          }
          summary={
            pending.kind === 'delete'
              ? t('waap_domain.custom_rules_delete_summary', 'Permanently delete custom rule "{{name}}".', { name: pending.rule.name })
              : t('waap_domain.custom_rules_save_summary', 'Save custom rule "{{name}}".', { name: pending.rule.name })
          }
          destructive={pending.kind === 'delete'}
          queryKeysToInvalidate={[actAsWAAPKeys.customRules(serviceId)]}
          mutationFn={buildMutationFn(pending)}
          onSuccess={() => { setPending(null); closeForm(); rulesQ.refetch(); }}
        />
      )}
    </div>
  );
}
